import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CampaignsCreateButton } from '../components/campaigns-create-button/campaigns-create-button';
import { CampaignsFiltersBar } from '../components/campaigns-filters/campaigns-filters';
import { CampaignsPagination } from '../components/campaigns-pagination/campaigns-pagination';
import { CampaignsTable } from '../components/campaigns-table/campaigns-table';
import { useSearchCampaignsRest } from '../hooks/use-campaigns-rest';
import type { CampaignsFilters, CampaignsSortKey } from '../types/campaigns.types';
import { DEFAULT_CAMPAIGNS_FILTERS } from '../utils/campaigns-filters.utils';
import { mapCampaignRestSummaryToCampaign } from '../utils/campaign-api-mapper';

export const CampaignsPage = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<CampaignsFilters>(DEFAULT_CAMPAIGNS_FILTERS);
  const [sortKey, setSortKey] = useState<CampaignsSortKey>('updatedAt');
  const [pageNo, setPageNo] = useState(1);
  const [pageSize, setPageSize] = useState(25);

  const query = useSearchCampaignsRest(search, filters, { pageNo, pageSize }, sortKey);
  const { data, isLoading, isError, error, isFetching } = query;

  const campaigns = useMemo(
    () => (data?.items ?? []).map(mapCampaignRestSummaryToCampaign),
    [data?.items]
  );

  const totalCount = data?.totalCount ?? 0;
  const totalPages = data?.totalPages ?? 0;

  const hasActiveFilters =
    search.trim().length > 0 ||
    filters.statuses.length > 0 ||
    (filters.channel ?? 'all') !== 'all';

  useEffect(() => {
    setPageNo(1);
  }, [search, filters, sortKey]);

  useEffect(() => {
    if (totalPages > 0 && pageNo > totalPages) {
      setPageNo(totalPages);
    }
  }, [pageNo, totalPages]);

  const handlePageSizeChange = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPageNo(1);
  };

  const clearFilters = () => {
    setSearch('');
    setFilters(DEFAULT_CAMPAIGNS_FILTERS);
    setPageNo(1);
  };

  return (
    <div className="flex h-full min-h-0 min-w-0 w-full max-w-full flex-col gap-5 overflow-hidden p-5 md:p-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-foreground">Campaigns</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {data
              ? `${totalCount.toLocaleString()} campaign${totalCount === 1 ? '' : 's'}`
              : 'Manage your email campaigns.'}
          </p>
        </div>
        <CampaignsCreateButton />
      </header>

      <CampaignsFiltersBar
        search={search}
        filters={filters}
        sortKey={sortKey}
        onSearchChange={setSearch}
        onFiltersChange={setFilters}
        onSortKeyChange={setSortKey}
      />

      <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-lg border bg-card shadow-e1">
        <div
          className={`min-h-0 flex-1 overflow-auto ${
            isFetching && !isLoading ? 'opacity-70 transition-opacity' : ''
          }`}
        >
          <CampaignsTable
            campaigns={campaigns}
            isEmpty={campaigns.length === 0}
            hasActiveFilters={hasActiveFilters}
            isLoading={isLoading}
            isError={isError}
            errorMessage={error instanceof Error ? error.message : undefined}
            onRetry={() => void query.refetch()}
            onClearFilters={clearFilters}
            onCreate={() => navigate('/campaigns/create-campaign')}
          />
        </div>

        <CampaignsPagination
          pageNo={pageNo}
          pageSize={pageSize}
          totalCount={totalCount}
          totalPages={totalPages}
          hasNextPage={Boolean(data?.hasNextPage)}
          hasPreviousPage={Boolean(data?.hasPreviousPage)}
          onPageChange={setPageNo}
          onPageSizeChange={handlePageSizeChange}
          className="px-4 py-3"
        />
      </div>
    </div>
  );
};
